import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  getLabById,
  getLabPollResponseByClient,
  submitLabPollResponse,
} from "../firebase";
import {
  formatLabDate,
  formatLabTimeRange,
  isFutureLab,
  isTodayInCentralTime,
} from "../utils/datetime";
import { getOrCreateClientId } from "../utils/clientIdentity";
import { normalizeLabActivities, normalizeLabPolls } from "../utils/labContent";
import AddToCalendarButtons from "./AddToCalendarButtons";
import Footer from "./Footer";
import QuestionForm from "./QuestionForm";
import RegistrationForm from "./RegistrationForm";
import SiteHeader from "./SiteHeader";

const copyTextToClipboard = async (value) => {
  const text = `${value || ""}`;
  if (!text) {
    return false;
  }

  if (typeof window !== "undefined" && window.navigator?.clipboard?.writeText) {
    await window.navigator.clipboard.writeText(text);
    return true;
  }

  if (typeof document === "undefined") {
    return false;
  }

  const temp = document.createElement("textarea");
  temp.value = text;
  temp.setAttribute("readonly", "");
  temp.style.position = "absolute";
  temp.style.left = "-9999px";
  document.body.appendChild(temp);
  temp.select();

  let copied = false;
  try {
    copied = document.execCommand("copy");
  } finally {
    document.body.removeChild(temp);
  }

  return copied;
};

function LabDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const [lab, setLab] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientId, setClientId] = useState("");
  const [pollDraftById, setPollDraftById] = useState({});
  const [pollUiById, setPollUiById] = useState({});
  const [copiedPromptKey, setCopiedPromptKey] = useState("");

  useEffect(() => {
    let isActive = true;

    const loadLab = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await getLabById(id);
        if (isActive) {
          setLab(response);
        }
      } catch (loadError) {
        console.error("Failed to load lab details:", loadError);
        if (isActive) {
          setError("We couldn't load this lab right now. Please try again soon.");
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadLab();

    return () => {
      isActive = false;
    };
  }, [id]);

  useEffect(() => {
    setClientId(getOrCreateClientId());
  }, []);

  const polls = useMemo(() => normalizeLabPolls(lab?.polls), [lab]);
  const activities = useMemo(() => normalizeLabActivities(lab?.activities), [lab]);
  const labIsFuture = isFutureLab(lab?.dateTime);
  const showLiveZoomButton = Boolean(
    lab?.zoomLink && !lab?.youtubeId && isTodayInCentralTime(lab?.dateTime)
  );
  const hubMode = Boolean(location.state?.hubMode || location.state?.portalMode);
  const backToPath = location.state?.backTo || "/";
  const topBackPath = "/hub";
  const isHubBackPath = backToPath === "/hub" || backToPath === "/portal";
  const backLabel =
    isHubBackPath
      ? "Back to School Leader AI Lab Hub"
      : backToPath === "/resources"
      ? "Back to Resources"
      : "Back to AI Lab Hub";
  const headerBackLabel =
    isHubBackPath
      ? "Back to hub"
      : backToPath === "/resources"
      ? "Back to resources"
      : "Back to home";

  useEffect(() => {
    setPollDraftById((previous) => {
      const next = {};
      polls.forEach((poll) => {
        next[poll.id] = previous[poll.id] || "";
      });
      return next;
    });

    setPollUiById((previous) => {
      const next = {};
      polls.forEach((poll) => {
        const existing = previous[poll.id] || {};
        next[poll.id] = {
          submitted: existing.submitted === true,
          isSubmitting: false,
          isChecking: false,
          error: "",
          success: existing.success || "",
        };
      });
      return next;
    });
  }, [polls]);

  useEffect(() => {
    if (!lab?.id || !clientId || polls.length === 0) {
      return undefined;
    }

    let isActive = true;

    const loadSubmissionState = async () => {
      setPollUiById((previous) => {
        const next = { ...previous };
        polls.forEach((poll) => {
          next[poll.id] = {
            ...(next[poll.id] || {}),
            isChecking: true,
            error: "",
          };
        });
        return next;
      });

      try {
        const results = await Promise.all(
          polls.map(async (poll) => {
            const existing = await getLabPollResponseByClient({
              labId: lab.id,
              pollId: poll.id,
              clientId,
            });

            return {
              pollId: poll.id,
              submitted: Boolean(existing),
            };
          })
        );

        if (!isActive) {
          return;
        }

        setPollUiById((previous) => {
          const next = { ...previous };
          results.forEach((entry) => {
            next[entry.pollId] = {
              ...(next[entry.pollId] || {}),
              submitted: entry.submitted,
              isChecking: false,
              success: entry.submitted
                ? "You already submitted this poll. You can view results."
                : "",
            };
          });
          return next;
        });
      } catch (loadError) {
        if (!isActive) {
          return;
        }

        console.error("Failed to load poll submission status:", loadError);
        setPollUiById((previous) => {
          const next = { ...previous };
          polls.forEach((poll) => {
            next[poll.id] = {
              ...(next[poll.id] || {}),
              isChecking: false,
            };
          });
          return next;
        });
      }
    };

    loadSubmissionState();

    return () => {
      isActive = false;
    };
  }, [clientId, lab?.id, polls]);

  const handlePollSubmit = async (pollId) => {
    const currentAnswer = `${pollDraftById[pollId] || ""}`.trim();

    if (!lab?.id || !clientId) {
      return;
    }

    if (!currentAnswer) {
      setPollUiById((previous) => ({
        ...previous,
        [pollId]: {
          ...(previous[pollId] || {}),
          error: "Please type your response before submitting.",
          success: "",
        },
      }));
      return;
    }

    setPollUiById((previous) => ({
      ...previous,
      [pollId]: {
        ...(previous[pollId] || {}),
        isSubmitting: true,
        error: "",
        success: "",
      },
    }));

    try {
      await submitLabPollResponse({
        labId: lab.id,
        pollId,
        clientId,
        answer: currentAnswer,
      });

      setPollUiById((previous) => ({
        ...previous,
        [pollId]: {
          ...(previous[pollId] || {}),
          submitted: true,
          isSubmitting: false,
          error: "",
          success: "Answer submitted. You can now view results.",
        },
      }));
    } catch (submitError) {
      const alreadySubmitted =
        submitError?.code === "already-submitted" ||
        `${submitError?.message || ""}`.includes("already-submitted");

      if (alreadySubmitted) {
        setPollUiById((previous) => ({
          ...previous,
          [pollId]: {
            ...(previous[pollId] || {}),
            submitted: true,
            isSubmitting: false,
            error: "",
            success: "You already submitted this poll. You can view results.",
          },
        }));
        return;
      }

      console.error("Failed to submit poll response:", submitError);
      setPollUiById((previous) => ({
        ...previous,
        [pollId]: {
          ...(previous[pollId] || {}),
          isSubmitting: false,
          error: "Could not submit right now. Please try again.",
          success: "",
        },
      }));
    }
  };

  const handleCopyPrompt = async (activityId, stepId, prompt) => {
    const copied = await copyTextToClipboard(prompt);
    if (!copied) {
      return;
    }

    const key = `${activityId}__${stepId}`;
    setCopiedPromptKey(key);
    window.setTimeout(() => {
      setCopiedPromptKey((current) => (current === key ? "" : current));
    }, 1800);
  };

  return (
    <>
      <SiteHeader
        registerHref={hubMode ? backToPath : "#register"}
        registerLabel={hubMode ? headerBackLabel : "Register free"}
      />

      <main className="lab-page-main">
        <div className="lab-detail-container">
          <Link className="text-link lab-back-link" to={topBackPath}>
            Back to School Leader AI Lab Hub
          </Link>

          {isLoading ? <p>Loading lab details...</p> : null}
          {error ? <p className="status-message error">{error}</p> : null}

          {!isLoading && !error && !lab ? (
            <section className="card">
              <h1>Lab not found</h1>
              <p>
                We couldn't find that session. It may have moved or the link may be
                incomplete.
              </p>
              <Link className="btn btn-primary" to="/">
                Return home
              </Link>
            </section>
          ) : null}

          {!isLoading && !error && lab ? (
            <article className="card lab-detail lab-detail-card">
              <div className="lab-detail-head">
                <p className="eyebrow">{formatLabDate(lab.dateTime)}</p>
                <h1 className="lab-detail-title">{lab.title}</h1>
                <p className="lab-time">
                  {formatLabTimeRange(lab.dateTime, lab.durationMinutes)}
                </p>
                {showLiveZoomButton ? (
                  <div className="lab-detail-live-row">
                    <a
                      className="btn btn-primary"
                      href={lab.zoomLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Join live on Zoom
                    </a>
                  </div>
                ) : null}
                <p className="long-description">{lab.longDescription}</p>
              </div>

              <section id="recording" className="lab-content-section">
                <h2 className="lab-section-title">Recording</h2>
                {lab.youtubeId ? (
                  <div className="video-wrap">
                    <iframe
                      title={`Replay for ${lab.title}`}
                      src={`https://www.youtube.com/embed/${lab.youtubeId}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="lab-recording-placeholder">
                    <p className="lab-recording-placeholder-title">
                      Recording will be posted after the live lab.
                    </p>
                    <p className="muted-copy">
                      Check back here after the session for the full replay.
                    </p>
                  </div>
                )}
              </section>

              <nav className="lab-section-nav" aria-label="Lab content sections">
                <div className="lab-section-nav-links">
                  <a href="#polls">📊 Polls</a>
                  <a href="#resources">📚 Resources</a>
                  <a href="#activities">🎲 Activities</a>
                </div>
              </nav>

              <section id="polls" className="lab-content-section">
                <h2 className="lab-section-title">📊 Polls</h2>
                {polls.length === 0 ? (
                  <p>No polls for this session right now.</p>
                ) : (
                  <div className="lab-poll-list">
                    {polls.map((poll, index) => {
                      const pollUi = pollUiById[poll.id] || {};
                      const answerValue = pollDraftById[poll.id] || "";

                      return (
                        <article className="card lab-poll-card" key={poll.id}>
                          <h3>Poll {index + 1}</h3>
                          <p>{poll.question}</p>

                          <label className="form-field" htmlFor={`poll-answer-${poll.id}`}>
                            <span>Your response</span>
                            <input
                              id={`poll-answer-${poll.id}`}
                              type="text"
                              value={answerValue}
                              onChange={(event) =>
                                setPollDraftById((previous) => ({
                                  ...previous,
                                  [poll.id]: event.target.value,
                                }))
                              }
                              placeholder="Type your answer here"
                              disabled={pollUi.submitted}
                            />
                          </label>

                          <div className="action-row">
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={() => handlePollSubmit(poll.id)}
                              disabled={
                                pollUi.submitted ||
                                pollUi.isSubmitting ||
                                pollUi.isChecking
                              }
                            >
                              {pollUi.submitted
                                ? "Submitted"
                                : pollUi.isSubmitting
                                ? "Submitting..."
                                : "Submit response"}
                            </button>

                            {pollUi.submitted ? (
                              <a
                                className="btn btn-outline"
                                href={`/lab/${lab.id}/poll/${poll.id}/results`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                View results
                              </a>
                            ) : null}
                          </div>

                          {pollUi.success ? (
                            <p className="status-message success">{pollUi.success}</p>
                          ) : null}
                          {pollUi.error ? (
                            <p className="status-message error">{pollUi.error}</p>
                          ) : null}
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>

              <section id="resources" className="lab-content-section">
                <h2 className="lab-section-title">📚 Resources</h2>
                {Array.isArray(lab.resources) && lab.resources.length > 0 ? (
                  <div className="lab-resource-grid">
                    {lab.resources.map((resource) => (
                      <a
                        key={`${resource.label}-${resource.url}`}
                        className="lab-resource-card"
                        href={resource.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <h3>{resource.label}</h3>
                        <p>Open resource</p>
                      </a>
                    ))}
                  </div>
                ) : (
                  <p>Resources will be shared here after the session.</p>
                )}
              </section>

              <section id="activities" className="lab-content-section">
                <h2 className="lab-section-title">🎲 Activities</h2>
                {activities.length === 0 ? (
                  <p>Activities will be posted here for this lab.</p>
                ) : (
                  <>
                    <div className="lab-activity-jump-nav" aria-label="Jump to activity">
                      {activities.map((activity, index) => (
                        <a
                          key={activity.id}
                          href={`#activity-${activity.id}`}
                          className="lab-activity-jump-node"
                          aria-label={`Go to Activity ${index + 1}: ${activity.title}`}
                        >
                          {index + 1}
                        </a>
                      ))}
                    </div>

                    <div className="lab-activity-list">
                      {activities.map((activity, activityIndex) => (
                        <article
                          id={`activity-${activity.id}`}
                          className="card lab-activity-card"
                          key={activity.id}
                        >
                          <h3>
                            Activity {activityIndex + 1}: {activity.title}
                          </h3>

                          {activity.links.length > 0 ? (
                            <>
                              <p className="lab-activity-subhead">Links needed</p>
                              <ul className="resource-list">
                                {activity.links.map((link) => (
                                  <li key={`${activity.id}-${link.label}-${link.url}`}>
                                    <a href={link.url} target="_blank" rel="noreferrer">
                                      {link.label}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </>
                          ) : null}

                          {activity.steps.length > 0 ? (
                            <>
                              <p className="lab-activity-subhead">Step-by-step directions</p>
                              <ol className="lab-activity-steps">
                                {activity.steps.map((step, stepIndex) => {
                                  const promptKey = `${activity.id}__${step.id}`;
                                  const isCopied = copiedPromptKey === promptKey;

                                  return (
                                    <li key={`${activity.id}-${step.id || stepIndex}`}>
                                      <p>
                                        <strong>Step {stepIndex + 1}:</strong>{" "}
                                        {step.text || "Step details coming soon."}
                                      </p>
                                      {step.prompt ? (
                                        <button
                                          type="button"
                                          className="lab-prompt-box"
                                          onClick={() =>
                                            handleCopyPrompt(activity.id, step.id, step.prompt)
                                          }
                                        >
                                          <span className="lab-prompt-copy-label">
                                            {isCopied
                                              ? "Prompt copied"
                                              : "Click to copy AI prompt"}
                                          </span>
                                          <code>{step.prompt}</code>
                                        </button>
                                      ) : null}
                                    </li>
                                  );
                                })}
                              </ol>
                            </>
                          ) : (
                            <p>Steps for this activity will be added soon.</p>
                          )}
                        </article>
                      ))}
                    </div>
                  </>
                )}
              </section>

              {!hubMode && isFutureLab(lab.dateTime) ? (
                <section className="future-actions">
                  <h2>Ready to join this Lab?</h2>
                  <div className="action-row">
                    <a className="btn btn-primary" href="#register">
                      Register
                    </a>
                  </div>
                  <AddToCalendarButtons lab={lab} />
                </section>
              ) : null}
            </article>
          ) : null}

          {!hubMode && !isLoading && !error && lab ? (
            <section id="register" className="card lab-register-card">
              <h2>
                {labIsFuture ? "Register for this AI Lab" : "Register for the next AI Lab"}
              </h2>
              <p>
                {labIsFuture
                  ? "Register in under 15 seconds and we'll send your invite and reminders."
                  : "Register once and we'll send you the invite and recording for each monthly AI Lab."}
              </p>

              <RegistrationForm
                nextLab={labIsFuture ? lab : null}
                preferredLab={labIsFuture ? lab : null}
                submitLabel={labIsFuture ? "Get my free invite" : "Get my invite"}
                source={{
                  sourceType: "lab-detail",
                  sourceSlug: lab.id,
                  sourceName: lab.title,
                }}
              />
            </section>
          ) : null}

          {!isLoading && !error && lab ? (
            <QuestionForm
              sectionId="lab-questions"
              sectionClassName="card lab-question-card"
              containerClassName="lab-question-card-inner"
              title="Questions or contact us"
              description="Need help with this Lab or want us to cover something in a future session? Send it here and we'll take a look."
            />
          ) : null}
        </div>
      </main>

      <Footer />
    </>
  );
}

export default LabDetailPage;
